import useAuth from '@app/auth/use-auth';
import { ccfRoutes } from '@app/ccf/ccf-routes';
import {
  CCFAddScope3TooltipContent,
  CCFCompanyReportsUpsellTooltipContent,
} from '@app/ccf/company-report/components/ccf-add-scope-3-tooltip';
import { useCCFUpsellDialog } from '@app/hooks/use-ccf-upsell-dialog';
import useUpsellDialog from '@app/hooks/use-upsell-dialog';
import { reportRoutes } from '@app/pages/reports/report-routes';
import { pcfRoutes } from '@app/pcf/pcf-routes';
import { purchasedGoodsRoutes } from '@app/purchased-goods/purchased-goods-routes';
import { soldGoodsRoutes } from '@app/sold-goods/sold-goods-routes';
import { useGetReports } from '@shared/api';
import { useGetImportsProgress } from '@shared/api/hooks/ccf/imports';
import {
  AccountResponse,
  ActivityImportProgress,
  ActivityImportStatusEnum,
} from '@shared/api/types';
import { Icons } from '@shared/components/content/icons';
import Logo from '@shared/components/logo/logo';
import { Badge } from '@shared/components/ui/badge';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarRail,
  useSidebar,
} from '@shared/components/ui/sidebar';
import { TooltipContent } from '@shared/components/ui/tooltip';
import { cn } from '@shared/lib/utils';
import * as React from 'react';
import { NavImportStatusBadge } from './nav-count-badge';
import { NavMain } from './nav-main';
import { NavUser } from './nav-user';

export type NavItem = {
  title: string;
  to: string;
  icon?: React.ReactNode;
  isActive?: boolean;
  disabled?: boolean;
  show?: boolean;
  tooltip?: string | React.ComponentProps<typeof TooltipContent>;
  badge?: React.ReactNode;
  testId?: string;
  /** Only highlight when the pathname matches `to` exactly. Use for parent
   *  routes whose path is also a prefix of other items (e.g. `/group`). */
  exact?: boolean;
};

export type MainNavItem = NavItem & {
  items?: NavItem[];
};

type SidebarImportStatus = 'loading' | 'error' | 'normal';

function useGetMainNavItems({
  user,
  importStatus,
  showUpsellDialog,
  showCCFUpsellDialog,
  hasLegacyReports,
}: {
  user: AccountResponse;
  importStatus?: {
    purchaseStatus: SidebarImportStatus;
    salesStatus: SidebarImportStatus;
  };
  showUpsellDialog?: () => void;
  showCCFUpsellDialog?: () => void;
  hasLegacyReports?: boolean;
}): MainNavItem[] {
  if (!user) {
    return [];
  }

  const { features } = user!;

  return [
    {
      title: 'Company',
      to: '#',
      icon: <Icons.company />,
      isActive: !!features.ccf,
      show: true,
      items: [
        {
          title: 'Company reports',
          to: ccfRoutes.COMPANY_REPORTS,
          icon: <Icons.clipboardList />,
          show: true,
          disabled: !features.ccf,
          tooltip: !features.ccf ? (
            <CCFCompanyReportsUpsellTooltipContent
              showDialog={showCCFUpsellDialog}
            />
          ) : undefined,
        },
        {
          title: 'Sites',
          to: ccfRoutes.SITES,
          icon: <Icons.factory />,
          show: true,
        },
      ],
    },
    {
      title: 'Purchased goods',
      to: '#',
      icon: <Icons.shoppingCart />,
      show: true,
      isActive: !!features.scope3,
      disabled: !features.scope3,
      tooltip:
        features.ccf && !features.scope3 ? (
          <CCFAddScope3TooltipContent showDialog={showUpsellDialog} />
        ) : undefined,
      items: [
        {
          title: 'Purchasing insights',
          to: purchasedGoodsRoutes.PURCHASED_INSIGHTS,
          icon: <Icons.lineChart />,
          show: true,
        },
        {
          title: 'Purchase reports',
          to: purchasedGoodsRoutes.PURCHASE_REPORTS,
          icon: <Icons.pieChart />,
          show: true,
        },
        {
          title: 'Food and drink',
          to: purchasedGoodsRoutes.PURCHASED_FOOD_AND_DRINK,
          icon: <Icons.carrot />,
          show: true,
        },
        {
          title: 'Packaging',
          to: purchasedGoodsRoutes.PURCHASED_PACKAGING,
          icon: <Icons.package />,
          show: true,
        },
        {
          title: 'Excluded Items',
          to: purchasedGoodsRoutes.PURCHASED_EXCLUDED_ITEMS,
          icon: <Icons.ban />,
          show: true,
        },
        {
          title: 'Purchase imports',
          to: purchasedGoodsRoutes.PURCHASED_GOODS_IMPORTS_LIST,
          icon: <Icons.import />,
          show: true,
          badge:
            importStatus?.purchaseStatus === 'error' ? (
              <NavImportStatusBadge type="error" />
            ) : importStatus?.purchaseStatus === 'loading' ? (
              <NavImportStatusBadge type="loading" />
            ) : undefined,
        },
      ],
    },
    {
      title: 'Products',
      to: '#',
      icon: <Icons.salad />,
      show: true,
      isActive: features.pcf,
      disabled: !features.pcf,
      items: [
        {
          title: 'Dashboard',
          to: pcfRoutes.PRODUCT_DASHBOARD,
          icon: <Icons.dashboard />,
          show: true,
        },
        {
          title: 'All products',
          to: pcfRoutes.PRODUCTS,
          icon: <Icons.salad />,
          show: true,
        },
        {
          title: 'Add product',
          to: pcfRoutes.PRODUCTS_CREATE,
          icon: <Icons.plus />,
          show: true,
          testId: 'add-product-link',
        },
        {
          title: 'Collections',
          to: pcfRoutes.COLLECTIONS,
          icon: <Icons.tags />,
          show: true,
        },
        {
          title: 'Components',
          to: pcfRoutes.PRODUCT_COMPONENTS,
          icon: <Icons.puzzle />,
          show: true,
        },
      ],
    },
    {
      title: 'Sold goods',
      to: '#',
      icon: <Icons.shoppingBag />,
      show: true,
      isActive: false,
      disabled: !features.salesReporting,
      items: [
        {
          title: 'Sales reports',
          to: soldGoodsRoutes.SALES_REPORTS,
          icon: <Icons.pieChart />,
          show: true,
        },
        {
          title: 'Food and drink',
          to: soldGoodsRoutes.SOLD_FOOD_AND_DRINK,
          icon: <Icons.carrot />,
          show: true,
        },
        {
          title: 'Packaging',
          to: soldGoodsRoutes.SOLD_PACKAGING,
          icon: <Icons.package />,
          show: true,
        },
        {
          title: 'Excluded Items',
          to: soldGoodsRoutes.SOLD_EXCLUDED_ITEMS,
          icon: <Icons.ban />,
          show: true,
        },
        {
          title: 'Sales imports',
          to: soldGoodsRoutes.SOLD_GOODS_IMPORTS_LIST,
          icon: <Icons.import />,
          show: true,
          badge:
            importStatus?.salesStatus === 'error' ? (
              <NavImportStatusBadge type="error" />
            ) : importStatus?.salesStatus === 'loading' ? (
              <NavImportStatusBadge type="loading" />
            ) : undefined,
        },
      ],
    },

    {
      title: 'Reports',
      icon: <Icons.lineChart />,
      to: reportRoutes.REPORTS,
      show: hasLegacyReports,
      badge: (
        <Badge variant="outline" size="sm">
          Legacy
        </Badge>
      ),
    },
  ];
}

const useImportSidebarStatus = () => {
  const { user } = useAuth();
  const enabled = !!user?.features.scope3;
  const { data: inFlightImports } = useGetImportsProgress({ enabled });

  return React.useMemo(() => {
    const getStatus = (
      importType: ActivityImportProgress['importType']
    ): SidebarImportStatus => {
      const imports =
        inFlightImports?.filter(
          (activityImport) => activityImport.importType === importType
        ) ?? [];
      if (
        imports.some(
          (activityImport) =>
            activityImport.status ===
              ActivityImportStatusEnum.processing_failed ||
            activityImport.numberOfFailedRows > 0
        )
      ) {
        return 'error';
      }
      if (
        imports.some(
          (activityImport) =>
            activityImport.status === ActivityImportStatusEnum.importing ||
            activityImport.status === ActivityImportStatusEnum.processing
        )
      ) {
        return 'loading';
      }
      return 'normal';
    };

    return {
      purchaseStatus: getStatus('purchased_goods'),
      salesStatus: getStatus('sold_goods'),
    };
  }, [inFlightImports]);
};

const useHasLegacyReports = () => {
  const {
    data: reportsData,
    isLoading: isQueryLoading,
    error,
  } = useGetReports();

  const hasLegacyReports = React.useMemo(() => {
    if (isQueryLoading || error || !reportsData) {
      return false;
    }

    return reportsData.length > 0;
  }, [error, isQueryLoading, reportsData]);

  return hasLegacyReports;
};

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { state } = useSidebar();
  const { user } = useAuth();
  const importStatus = useImportSidebarStatus();
  const hasLegacyReports = useHasLegacyReports();
  const { showDialog, UpsellDialogComponent } = useCCFUpsellDialog();
  const {
    showDialog: showCCFUpsellDialog,
    UpsellDialogComponent: CCFUpsellDialogComponent,
  } = useUpsellDialog();

  const mainNav = useGetMainNavItems({
    user: user!,
    importStatus,
    hasLegacyReports,
    showUpsellDialog: showDialog,
    showCCFUpsellDialog,
  });

  const collapsed = state === 'collapsed';
  const expanded = state === 'expanded';

  return (
    <Sidebar collapsible="icon" {...props}>
      {UpsellDialogComponent}
      {CCFUpsellDialogComponent}
      <SidebarHeader
        className={cn('flex h-24 justify-center bg-teal-50', {
          'pl-5': expanded,
        })}
      >
        {collapsed ? (
          <Logo
            variant="icon"
            color="dark"
            className={cn('w-44', {
              'w-8 mx-auto': collapsed,
            })}
          />
        ) : (
          <Logo
            variant="name"
            color="dark"
            className={cn('w-44', {
              'w-8 mx-auto': collapsed,
            })}
          />
        )}
      </SidebarHeader>
      <SidebarContent
        className={cn('px-2', {
          'mx-auto': collapsed,
        })}
      >
        <NavMain items={mainNav} />
      </SidebarContent>
      <SidebarFooter
        className={cn('px-2', {
          'mx-auto': collapsed,
        })}
      >
        <NavUser />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
